import { ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, DraggableLocation, Droppable, DropResult } from 'react-beautiful-dnd';
import { Virtuoso } from 'react-virtuoso';

import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useTranslation } from '@/hooks/useTranslation';
import { DataModelAttribute } from '@/types/DataModel';

import { DraggableAttribute } from './DraggableAttribute';
import { HeightPreservingItem } from './HeightPreservingItem';

const DROP_ID_ALL_ATTRIBUTES = 'allAttributes';
const DROP_ID_SELECTED_ATTRIBUTES = 'selectedAttributes';

const DataModelAttributesSelector = ({ attributes, onSelectedChange, selectedAttributes=[] }:
        { attributes: DataModelAttribute[] , onSelectedChange: any, selectedAttributes?:DataModelAttribute[]}) => {

    const { t } = useTranslation();
    const { translateAttributeLabel } = useAttributeLabelTranslator();

    const [allAttributes, setAllAttributes] = useState<DataModelAttribute[]>(attributes);
    const [selected, setSelected] = useState<DataModelAttribute[]>(selectedAttributes);

    useEffect(()=>{
        onSelectedChange (selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[selected]);

    const [ textFilter, setTextFilter ] = useState<string | undefined>('');
    const onTextChange = (text: string | undefined) => {

        let filteredAttributes: DataModelAttribute[] = [];

        if (text && text != '') {

            filteredAttributes = attributes.filter(attribute => {

                const attributeLabel = translateAttributeLabel(attribute);
                const matches = attributeLabel
                    .toLowerCase()
                    .includes(text.toLowerCase());
                const notAlreadySelected = !selected.find(selectedAttribute => selectedAttribute.name === attribute.name);

                return matches && notAlreadySelected;
            });

        } else {
            // Reset all available attributes, except the ones already selected
            filteredAttributes = attributes.filter(attribute =>
                !selected.find(selectedAttribute => selectedAttribute.name === attribute.name));

        }

        setAllAttributes(filteredAttributes);
        setTextFilter(text);
    };

    const onDragEnd = (result: DropResult) => {

        const { source, destination } = result;

        // dropped outside the list
        if (!destination) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            // Reorder in case it was dropped on the same area it was picked from
            if (source.droppableId === DROP_ID_ALL_ATTRIBUTES) {
                const reordered = reorder(allAttributes, source.index, destination.index);
                setAllAttributes(reordered);
            } else if (source.droppableId === DROP_ID_SELECTED_ATTRIBUTES) {
                const reordered = reorder(selected, source.index, destination.index);
                setSelected(reordered);
                onSelectedChange(selected);
            }
        } else {
            move(source, destination);
        }
    };

    const reorder = (list: DataModelAttribute[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const move = (droppableSource: DraggableLocation, droppableDestination: DraggableLocation) => {

        if (droppableSource.droppableId === DROP_ID_ALL_ATTRIBUTES) {

            const [removed] = allAttributes.splice(droppableSource.index, 1);
            selected.splice(droppableDestination.index, 0, removed);
            setAllAttributes([...allAttributes]);
            setSelected([...selected]);

        } else {

            const [removed] = selected.splice(droppableSource.index, 1);
            allAttributes.splice(droppableDestination.index, 0, removed);

            setSelected([...selected]);
            setAllAttributes([...allAttributes]);

        }
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <style>
                {`
                .height-preserving-container:empty {
                    min-height: calc(var(--child-height));
                    box-sizing: border-box;
                }
                `}
            </style>
            <div className="tw-flex-1 tw-flex tw-flex-row tw-space-x-2 tw-h-full tw-min-h-0">
                <Droppable
                    droppableId={DROP_ID_SELECTED_ATTRIBUTES}
                    renderClone={(provided, _, rubric) =>
                        <DraggableAttribute provided={provided} attribute={selected[rubric.source.index]} />
                    }>
                    {(provided, snapshot) =>
                        <div
                            className={`tw-flex-1 tw-flex tw-flex-col tw-items-center tw-border tw-border-dashed tw-border-gray-200 tw-overflow-y-auto ${snapshot.isDraggingOver && 'ts-bg-gray-50'}`}
                            {...provided.droppableProps}
                            ref={provided.innerRef}>
                            {selected.length === 0 ? (
                                <div className="tw-w-full tw-flex tw-flex-row tw-items-center tw-justify-center tw-space-x-2">
                                    <Icon icon={ChevronDownIcon} size="lg" variant="dark" colorScheme="light" />
                                    <p className="tw-text-gray-400 tw-m-2">{t('ATTRIBUTES_SELECTOR.DRAG_DROP_HINT')}</p>
                                </div>
                            ) : (
                                selected.map((attribute, index) =>
                                    <Draggable draggableId={`selected-${attribute.name}`} index={index} key={attribute.name}>
                                        {provided => <DraggableAttribute attribute={attribute} isSelected={true} provided={provided} />}
                                    </Draggable>
                                )
                            )}
                            {provided.placeholder}
                        </div>
                    }
                </Droppable>
                <div className="tw-w-full tw-flex tw-flex-1 tw-flex-col">
                    <Input
                        data-testid="input-search-attributes"
                        icon={FunnelIcon}
                        placeholder={t('ATTRIBUTES_SELECTOR.SEARCH_ATTRIBUTES')}
                        value={textFilter}
                        onChange={e => onTextChange(e.target.value)}
                    />
                    <p className="tw-text-right tw-text-t-sm tw-text-gray-400 tw-m-0 tw-mb-2">Total attributes: {attributes.length}</p>
                    <div className="tw-flex-1">
                        <Droppable
                            droppableId={DROP_ID_ALL_ATTRIBUTES}
                            mode="virtual"
                            renderClone={(provided, _, rubric) =>
                                <DraggableAttribute provided={provided} attribute={allAttributes[rubric.source.index]} />
                            }
                        >
                            {(provided, snapshot) =>
                                <Virtuoso
                                    className={`tw-flex-1 tw-relative ${snapshot.isDraggingOver ? 'tw-bg-gray-50' : ''}`}
                                    components={{
                                        Item: HeightPreservingItem
                                    }}
                                    data={allAttributes}
                                    //@ts-ignore
                                    scrollerRef={provided.innerRef}
                                    style={{ height: '100%', width: '100%' }}
                                    itemContent={(index, item) =>
                                        <Draggable draggableId={`selectable-${item.name}`} index={index} key={item.name}>
                                            {provided => <DraggableAttribute key={item.name} attribute={item} provided={provided} />}
                                        </Draggable>
                                    }>
                                </Virtuoso>
                            }
                        </Droppable>
                    </div>
                </div>
            </div>
        </DragDropContext>
    );
};

export { DataModelAttributesSelector };
